/**
 * AdminEntityDrawer (P15.1 / #416).
 *
 * Slide-in panel for create / edit / delete of one Foundation row.
 * Renders form fields from `adapter.formFields`; type-discriminated
 * (text / textarea / number / decimal / bool / select / parentSelect).
 *
 * State machine:
 *   - mode="create" -> formValues = adapter.formInitial()
 *   - mode="edit"   -> formValues = adapter.rowToPatch(detail) once
 *                      the detail fetch resolves
 *
 * Errors surfaced inline:
 *   - 409 -> field-level error on the `code` input (uniqueness)
 *   - 422 -> top-of-form banner with the backend `detail`
 *   - any other ApiError -> top-of-form banner
 *
 * Module-boundary rule: imports from `foundation/*` + `shared/*`
 * only -- eslint enforces.
 */
import { type ChangeEvent, type FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebouncedValue } from "shared/hooks/useDebouncedValue";
import { useQuery } from "@tanstack/react-query";

import { api } from "shared/api/client";
import { useOptionalToast } from "shared/toast/ToastProvider";

import {
  useCreateFoundationEntity,
  useDeleteFoundationEntity,
  useFoundationAdminDetail,
  usePatchFoundationEntity,
} from "./useFoundationAdminList";
import { datetimeLocalToIso, isoToDatetimeLocal } from "./datetimeFormat";
import type { EntityAdapter, FormField } from "./types";
import { useDateInputLang } from "shared/i18n/dateInputLang";

export interface AdminEntityDrawerProps<TRow, TIn, TPatch> {
  adapter: EntityAdapter<TRow, TIn, TPatch>;
  /** "create" opens a fresh form; "edit" loads the row by id first. */
  mode: "create" | "edit";
  /** Required when mode === "edit". */
  editId?: string | undefined;
  /** Fires on Cancel / X / successful save. */
  onClose: () => void;
}

export function AdminEntityDrawer<TRow, TIn, TPatch>({
  adapter,
  mode,
  editId,
  onClose,
}: AdminEntityDrawerProps<TRow, TIn, TPatch>) {
  const { t } = useTranslation();
  const detail = useFoundationAdminDetail(
    adapter,
    mode === "edit" ? editId : undefined,
  );
  const createMut = useCreateFoundationEntity(adapter);
  const patchMut = usePatchFoundationEntity(adapter);
  const toast = useOptionalToast();
  const deleteMut = useDeleteFoundationEntity(adapter);

  const [values, setValues] = useState<Record<string, unknown>>(
    () => adapter.formInitial() as Record<string, unknown>,
  );
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  /* ROUND 15 F6 -- a dialog closes on Escape, as every other one does;
     this one did not, and a reader mid-edit had to find the Cancel. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Hydrate the form from the detail fetch on edit-mode mount.
  useEffect(() => {
    if (mode === "edit" && detail.data) {
      setValues(adapter.rowToPatch(detail.data) as Record<string, unknown>);
    }
  }, [mode, detail.data, adapter]);

  const handleChange = (key: string, next: unknown) =>
    setValues((prev) => ({ ...prev, [key]: next }));

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (mode === "create") {
      createMut.mutate(values as TIn, {
        onSuccess: () => {
          if (adapter.createdToastKey && toast) {
            toast.push({
              tone: "success",
              message: t(adapter.createdToastKey, { name: String(values.name ?? "") }),
            });
          }
          onClose();
        },
      });
    } else if (editId) {
      patchMut.mutate(
        { id: editId, body: values as TPatch },
        { onSuccess: () => onClose() },
      );
    }
  };

  const onDelete = () => {
    if (!editId) return;
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    deleteMut.mutate({ id: editId }, { onSuccess: () => onClose() });
  };

  const activeError =
    (mode === "create" ? createMut.error : patchMut.error) ?? deleteMut.error;
  const codeError = activeError?.status === 409 ? activeError.detail : null;
  const formError =
    activeError && activeError.status !== 409 ? activeError.detail : null;

  const title =
    mode === "create"
      ? t(`${adapter.i18nPrefix}.drawer.createTitle`)
      : ((detail.data && (detail.data as { name?: string }).name) ??
        t(`${adapter.i18nPrefix}.drawer.editTitleFallback`));

  const submitting = createMut.isPending || patchMut.isPending;

  return (
    <aside
      data-testid="admin-entity-drawer"
      data-mode={mode}
      role="dialog"
      aria-label={title}
      className="fixed inset-y-0 end-0 w-full max-w-md bg-white border-s border-slate-200 shadow-xl overflow-y-auto z-30"
    >
      <header className="flex items-baseline justify-between p-4 border-b border-slate-200">
        <h2 className="text-base font-semibold text-slate-900 truncate">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          data-testid="admin-entity-drawer-close"
          className="text-slate-500 hover:text-slate-900 px-2"
          aria-label={t(`${adapter.i18nPrefix}.drawer.closeLabel`)}
        >
          ×
        </button>
      </header>

      {mode === "edit" && detail.isLoading && (
        <p
          data-testid="admin-entity-drawer-loading"
          className="p-4 text-sm text-slate-500"
        >
          {t(`${adapter.i18nPrefix}.drawer.loading`)}
        </p>
      )}

      {(mode === "create" || detail.data) && (
        <form onSubmit={onSubmit} className="p-4 space-y-4">
          {formError && (
            <p
              data-testid="admin-entity-drawer-form-error"
              className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2"
            >
              {formError}
            </p>
          )}
          {(() => {
            const renderRow = (field: FormField<TIn>) =>
              mode === "edit" && field.kind === "text" && field.createOnly ? (
                <StoredField
                  key={field.key as string}
                  fieldKey={field.key as string}
                  label={field.label}
                  value={
                    (detail.data as Record<string, unknown> | undefined)?.[
                      field.key as string
                    ]
                  }
                  hint={t("modules.foundation.admin.fixedOnceCreated")}
                />
              ) : (
                <FieldRow<TIn>
                  key={field.key as string}
                  field={field}
                  value={values[field.key as string]}
                  onChange={(next) => handleChange(field.key as string, next)}
                  codeError={field.key === "code" ? codeError : null}
                />
              );
            // Ungrouped fields render flat at the top (core identity);
            // grouped fields collapse under their section, first-seen
            // order (P136).
            const ungrouped = adapter.formFields.filter((f) => !f.group);
            const groupOrder: string[] = [];
            const byGroup = new Map<string, FormField<TIn>[]>();
            for (const f of adapter.formFields) {
              if (!f.group) continue;
              if (!byGroup.has(f.group)) {
                byGroup.set(f.group, []);
                groupOrder.push(f.group);
              }
              byGroup.get(f.group)!.push(f);
            }
            return (
              <>
                {ungrouped.map(renderRow)}
                {groupOrder.map((g) => (
                  <details
                    key={g}
                    data-testid={`admin-drawer-group-${g
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, "-")
                      .replace(/^-|-$/g, "")}`}
                    className="rounded border border-slate-200"
                  >
                    <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                      {g}
                    </summary>
                    <div className="space-y-3 px-3 pb-3 pt-1">
                      {(byGroup.get(g) ?? []).map(renderRow)}
                    </div>
                  </details>
                ))}
              </>
            );
          })()}
          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100">
            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-entity-drawer-submit"
              className="px-3 py-1.5 rounded bg-brand-brass text-brand-navy text-sm font-medium hover:bg-brand-brassDark disabled:opacity-50"
            >
              {submitting
                ? t(`${adapter.i18nPrefix}.drawer.saving`)
                : t(`${adapter.i18nPrefix}.drawer.save`)}
            </button>
            <button
              type="button"
              onClick={onClose}
              data-testid="admin-entity-drawer-cancel"
              className="px-3 py-1.5 rounded border border-slate-300 text-slate-700 text-sm hover:bg-slate-50"
            >
              {t(`${adapter.i18nPrefix}.drawer.cancel`)}
            </button>
            {mode === "edit" && (
              <button
                type="button"
                onClick={onDelete}
                data-testid="admin-entity-drawer-delete"
                className="ms-auto px-3 py-1.5 rounded border border-red-300 text-red-700 text-sm hover:bg-red-50"
              >
                {confirmingDelete
                  ? t(`${adapter.i18nPrefix}.drawer.deleteConfirm`)
                  : t(`${adapter.i18nPrefix}.drawer.delete`)}
              </button>
            )}
          </div>
        </form>
      )}

      {mode === "edit" && editId && adapter.extraSections && (
        <div
          data-testid="admin-entity-drawer-extras"
          className="border-t border-slate-200 p-4 space-y-4 bg-slate-50"
        >
          {adapter.extraSections.map((Section, i) => (
            <Section key={i} rowId={editId} />
          ))}
        </div>
      )}
    </aside>
  );
}

/** Round 18 G26 -- a stored value that may not change, shown as such. */
function StoredField({
  fieldKey,
  label,
  value,
  hint,
}: {
  fieldKey: string;
  label: string;
  value: unknown;
  hint: string;
}) {
  return (
    <div>
      <label
        htmlFor={`field-${fieldKey}`}
        className="block text-xs font-medium text-slate-700 uppercase tracking-wide"
      >
        {label}
      </label>
      <input
        id={`field-${fieldKey}`}
        type="text"
        value={value == null ? "" : String(value)}
        readOnly
        data-testid={`admin-field-${fieldKey}`}
        className="mt-1 block w-full rounded border border-slate-200 bg-slate-50 px-2 py-1 text-sm text-slate-700"
      />
      <p className="mt-0.5 text-[11px] text-slate-500">{hint}</p>
    </div>
  );
}

interface FieldRowProps<TIn> {
  field: FormField<TIn>;
  value: unknown;
  onChange: (next: unknown) => void;
  codeError: string | null;
}

function FieldRow<TIn>({ field, value, onChange, codeError }: FieldRowProps<TIn>) {
  const dateInputLang = useDateInputLang();
  const fieldKey = field.key as string;
  const fieldId = `field-${fieldKey}`;
  const label = (
    <label
      htmlFor={fieldId}
      className="block text-xs font-medium text-slate-700 uppercase tracking-wide"
    >
      {field.label}
    </label>
  );
  const inputBase =
    "mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-emerald-500 focus:ring-emerald-500";

  let control: React.ReactNode;
  if (field.kind === "text") {
    control = (
      <input
        id={fieldId}
        type="text"
        value={(value as string) ?? ""}
        maxLength={field.maxLength}
        required={field.required}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        data-testid={`admin-field-${fieldKey}`}
        className={inputBase}
      />
    );
  } else if (field.kind === "textarea") {
    control = (
      <textarea
        id={fieldId}
        rows={field.rows ?? 3}
        value={(value as string) ?? ""}
        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
        data-testid={`admin-field-${fieldKey}`}
        className={inputBase}
      />
    );
  } else if (field.kind === "number") {
    control = (
      <input
        id={fieldId}
        type="number"
        value={(value as number | undefined) ?? ""}
        min={field.min}
        max={field.max}
        required={field.required}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          onChange(e.target.value === "" ? null : Number(e.target.value))
        }
        data-testid={`admin-field-${fieldKey}`}
        className={inputBase}
      />
    );
  } else if (field.kind === "decimal") {
    // Decimal renders as a free-text input so values like "1850000.00"
    // round-trip without JS Number precision loss. Wire format is
    // string-as-Decimal per the backend Pydantic v2 default.
    control = (
      <input
        id={fieldId}
        type="text"
        inputMode="decimal"
        value={(value as string | null) ?? ""}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          onChange(e.target.value === "" ? null : e.target.value)
        }
        placeholder={field.currencyHint === "SAR" ? "0.00 SAR" : "0.00"}
        data-testid={`admin-field-${fieldKey}`}
        className={inputBase}
      />
    );
  } else if (field.kind === "bool") {
    control = (
      <label className="inline-flex items-center gap-2 mt-1">
        <input
          id={fieldId}
          type="checkbox"
          checked={!!value}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
          data-testid={`admin-field-${fieldKey}`}
        />
        <span className="text-sm text-slate-700">{field.label}</span>
      </label>
    );
    return <div>{control}</div>;
  } else if (field.kind === "datetime") {
    control = (
      <input
        id={fieldId}
        type="datetime-local"
        lang={dateInputLang}
        value={isoToDatetimeLocal(value as string | null)}
        required={field.required}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          onChange(datetimeLocalToIso(e.target.value))
        }
        data-testid={`admin-field-${fieldKey}`}
        className={inputBase}
      />
    );
  } else if (field.kind === "date") {
    control = (
      <input
        id={fieldId}
        type="date"
        lang={dateInputLang}
        value={(value as string | null) ?? ""}
        required={field.required}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          onChange(e.target.value || null)
        }
        data-testid={`admin-field-${fieldKey}`}
        className={inputBase}
      />
    );
  } else if (field.kind === "select") {
    control = (
      <select
        id={fieldId}
        value={(value as string) ?? ""}
        required={field.required}
        onChange={(e: ChangeEvent<HTMLSelectElement>) => onChange(e.target.value)}
        data-testid={`admin-field-${fieldKey}`}
        className={inputBase}
      >
        {!field.required && <option value="">--</option>}
        {field.options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    );
  } else if (field.kind === "parentSelect") {
    // parentSelect: fetch the OTHER same-kind rows for the picker.
    // (P15.2 / #416)
    control = (
      <ParentSelect
        fieldId={fieldId}
        fieldKey={fieldKey}
        value={(value as string | null) ?? null}
        entityKind={field.entityKind}
        onChange={(next) => onChange(next)}
        inputBase={inputBase}
      />
    );
  } else {
    // entityPicker: cross-entity FK picker (e.g. Journey.ibs_service_id
    // points at IBSService). Generalises parentSelect for any of the
    // 4 supported entity kinds (P16.3 / #485).
    control = (
      <EntityPicker
        fieldId={fieldId}
        fieldKey={fieldKey}
        value={(value as string | null) ?? null}
        entityKind={field.entityKind}
        required={field.required ?? false}
        onChange={(next) => onChange(next)}
        inputBase={inputBase}
      />
    );
  }

  return (
    <div>
      {label}
      {control}
      {codeError && (
        <p
          data-testid={`admin-field-${fieldKey}-error`}
          className="mt-1 text-xs text-red-700"
        >
          {codeError}
        </p>
      )}
    </div>
  );
}

/**
 * Round 14 E4 -- a picker that can reach every row, not the first 500.
 *
 * The capped list was honest (round 13 C7 made it SAY that 1,903 of
 * 2,404 services were not listed) and still could not offer them. So
 * where the list is capped a search box appears, and a term of two or
 * more characters asks the register's own `q` search for matches. The
 * current value is always offered, even when it is not in the first
 * page, or the select would silently show nothing selected.
 */
function usePickerOptions(base: string, cacheKind: string) {
  const [search, setSearch] = useState("");
  const term = useDebouncedValue(search.trim(), 250);
  const sep = base.includes("?") ? "&" : "?";
  const first = useQuery({
    queryKey: ["foundation-admin", cacheKind, "picker-first-page"] as const,
    queryFn: () =>
      api.getList<{ id: string; name: string; code: string }[]>(
        `${base}${sep}limit=500`,
      ),
    staleTime: 5 * 60_000,
  });
  const found = useQuery({
    queryKey: ["foundation-admin", cacheKind, "picker-search", term] as const,
    queryFn: () =>
      api.getList<{ id: string; name: string; code: string }[]>(
        `${base}${sep}limit=50&q=${encodeURIComponent(term)}`,
      ),
    enabled: term.length >= 2,
    staleTime: 60_000,
  });
  const firstRows = Array.isArray(first.data?.rows) ? first.data.rows : [];
  const total = first.data?.total ?? null;
  const missing = total !== null ? Math.max(0, total - firstRows.length) : 0;
  const searching = term.length >= 2;
  const rows = searching
    ? Array.isArray(found.data?.rows)
      ? found.data.rows
      : []
    : firstRows;
  return {
    search,
    setSearch,
    searching,
    rows,
    firstRows,
    shown: firstRows.length,
    missing,
    loading: first.isLoading,
  };
}

function withCurrent(
  rows: { id: string; name: string; code: string }[],
  firstRows: { id: string; name: string; code: string }[],
  value: string | null,
  label: string,
) {
  if (!value || rows.some((r) => r.id === value)) return rows;
  const known = firstRows.find((r) => r.id === value);
  return [known ?? { id: value, name: label, code: "" }, ...rows];
}

function PickerSearch({
  fieldKey,
  picker,
}: {
  fieldKey: string;
  picker: ReturnType<typeof usePickerOptions>;
}) {
  const { t } = useTranslation();
  if (picker.missing === 0 && !picker.searching) return null;
  return (
    <>
      <input
        type="search"
        dir="auto"
        data-testid={`admin-field-${fieldKey}-search`}
        aria-label={t("screens.adminEntityDrawer.pickerSearch")}
        placeholder={t("screens.adminEntityDrawer.pickerSearch")}
        value={picker.search}
        onChange={(e) => picker.setSearch(e.target.value)}
        className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-xs"
      />
      <p
        data-testid={`admin-field-${fieldKey}-capped`}
        className="mt-1 text-[11px] text-rag-amber"
      >
        {picker.searching
          ? t("screens.adminEntityDrawer.pickerMatches", { count: picker.rows.length })
          : t("screens.adminEntityDrawer.optionsCapped", {
              shown: picker.shown,
              count: picker.missing,
            })}
      </p>
    </>
  );
}

/**
 * Parent picker for self-referential FKs (Service.parent_service_id,
 * Capability.parent_capability_id). Lets the admin pick null for "no
 * parent" (top-of-tree).
 */
function ParentSelect({
  fieldId,
  fieldKey,
  value,
  entityKind,
  onChange,
  inputBase,
}: {
  fieldId: string;
  fieldKey: string;
  value: string | null;
  entityKind: "service" | "capability";
  onChange: (next: string | null) => void;
  inputBase: string;
}) {
  const { t } = useTranslation();
  const base =
    entityKind === "service"
      ? "/v1/admin/foundation/services"
      : "/v1/admin/foundation/capabilities";
  const picker = usePickerOptions(base, entityKind);
  const options = withCurrent(
    picker.rows,
    picker.firstRows,
    value,
    t("screens.adminEntityDrawer.currentSelection"),
  );
  return (
    <>
      <select
        id={fieldId}
        value={value ?? ""}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          onChange(e.target.value === "" ? null : e.target.value)
        }
        data-testid={`admin-field-${fieldKey}`}
        className={inputBase}
        disabled={picker.loading}
      >
        <option value="">{t("screens.adminEntityDrawer.noParent")}</option>
        {options.map((p) => (
          <option key={p.id} value={p.id}>
            {p.code ? `${p.name} (${p.code})` : p.name}
          </option>
        ))}
      </select>
      <PickerSearch fieldKey={fieldKey} picker={picker} />
    </>
  );
}

/**
 * EntityPicker: a cross-entity select for non-parent FK fields
 * (P16.3 / #485). Used by the Journey admin to pick the IBS + the
 * Channel a journey belongs to. Shares the searchable options with
 * ParentSelect (round 14 E4).
 */
function EntityPicker({
  fieldId,
  fieldKey,
  value,
  entityKind,
  required,
  onChange,
  inputBase,
}: {
  fieldId: string;
  fieldKey: string;
  value: string | null;
  entityKind: "ibs" | "service" | "capability" | "channel" | "vendor";
  required: boolean;
  onChange: (next: string | null) => void;
  inputBase: string;
}) {
  const { t } = useTranslation();
  const base =
    entityKind === "ibs"
      ? "/v1/admin/foundation/ibses"
      : entityKind === "channel"
        ? "/v1/admin/foundation/channels"
        : entityKind === "vendor"
          ? "/v1/admin/foundation/vendors"
          : entityKind === "service"
            ? "/v1/admin/foundation/services"
            : "/v1/admin/foundation/capabilities";
  const picker = usePickerOptions(base, entityKind);
  const options = withCurrent(
    picker.rows,
    picker.firstRows,
    value,
    t("screens.adminEntityDrawer.currentSelection"),
  );
  return (
    <>
      <select
        id={fieldId}
        value={value ?? ""}
        required={required}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          onChange(e.target.value === "" ? null : e.target.value)
        }
        data-testid={`admin-field-${fieldKey}`}
        className={inputBase}
        disabled={picker.loading}
      >
        {!required && <option value="">--</option>}
        {required && <option value="">{t("screens.adminEntityDrawer.select")}</option>}
        {options.map((p) => (
          <option key={p.id} value={p.id}>
            {p.code ? `${p.name} (${p.code})` : p.name}
          </option>
        ))}
      </select>
      <PickerSearch fieldKey={fieldKey} picker={picker} />
    </>
  );
}
